import React from 'react';

// Catches render-time errors in its subtree so one broken page/component
// shows a recoverable fallback instead of leaving the user on a blank white
// screen. Does not catch errors from event handlers, async code, or
// server-side rendering (React only ever catches render/lifecycle errors).
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  componentDidUpdate(prevProps) {
    // Allow the parent to clear the error by changing resetKey (e.g. on
    // navigation), without needing to remount the boundary via `key`.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || <DefaultErrorFallback />;
    }
    return this.props.children;
  }
}

function DefaultErrorFallback() {
  return (
    <div className="flex flex-col items-center justify-center gap-4 p-10 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
        <svg className="h-7 w-7 text-red-500" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" />
          <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <path d="M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </div>
      <div>
        <p className="text-base font-bold text-gray-700">Something went wrong</p>
        <p className="mt-1 text-sm text-gray-500">This page ran into an unexpected error. Try reloading.</p>
      </div>
      <button
        onClick={() => window.location.reload()}
        className="rounded-2xl bg-[#0071BC] px-4 py-2 text-sm font-bold text-white hover:opacity-80"
      >
        Reload
      </button>
    </div>
  );
}

export default ErrorBoundary;
